import type { MNHotInstance } from '../../../../bridge/tableHotInstanceTypes';
import type { ManualColumnLiveResizeSession } from '../../../../bridge/utils/hotInstanceUtils/columnSize/ManualColumnLiveResizeSession';

const DEFAULT_MANUAL_COLUMN_LIVE_RESIZE_SESSION: ManualColumnLiveResizeSession = {
    selectedCols: [],
    initialWidthsPx: [],
    dragStartX: 0,
    moved: false,
};

/**
 * Reads Milanote-owned resize session state from the Handsontable instance.
 */
export const getManualColumnLiveResizeSession = (hotInstance: MNHotInstance): ManualColumnLiveResizeSession => {
    const session = hotInstance.milanoteProps.manualColumnLiveResizeSession;

    return session || DEFAULT_MANUAL_COLUMN_LIVE_RESIZE_SESSION;
};

/**
 * Starts a fresh Milanote-owned column live resize session on the Handsontable instance.
 */
export const initialiseManualLiveResizeSession = (
    hotInstance: MNHotInstance,
    session: Omit<ManualColumnLiveResizeSession, 'moved'>,
): ManualColumnLiveResizeSession => {
    hotInstance.milanoteProps.manualColumnLiveResizeSession = {
        ...session,
        moved: false,
    };

    return hotInstance.milanoteProps.manualColumnLiveResizeSession;
};

/**
 * Marks the current Milanote-owned column live resize session as having received pointer movement.
 */
export const markManualColumnLiveResizeSessionMoved = (hotInstance: MNHotInstance): ManualColumnLiveResizeSession => {
    hotInstance.milanoteProps.manualColumnLiveResizeSession = {
        ...getManualColumnLiveResizeSession(hotInstance),
        moved: true,
    };

    return hotInstance.milanoteProps.manualColumnLiveResizeSession;
};

/**
 * Removes Milanote-owned resize session state from the Handsontable instance.
 */
export const clearManualColumnLiveResizeSession = (hotInstance: MNHotInstance | null | undefined): void => {
    if (!hotInstance) return;

    delete hotInstance.milanoteProps.manualColumnLiveResizeSession;
};

/**
 * Indicates whether Milanote currently owns an in-progress column resize gesture for this table.
 */
export const isHotTableColumnLiveResizeInProgress = (hotInstance: MNHotInstance | null | undefined): boolean =>
    !!hotInstance?.milanoteProps?.manualColumnLiveResizeSession;
